import { useState, useEffect, useCallback } from 'react';
import { AgxDivider } from '@urbanx/agx-ui-components';
import { useAppSelector } from 'hooks/useAppSelector';
import DepositPayable from './DepositPayable';
import { ContractDepositModel, ContractDepositErrorState } from './types';
import { MaxDepositPercentage, ErrorMessage, CalculateAmount } from './Deposit';
import BalanceDeposit from './BalanceDeposit';
import './ContractDeposit.scss';
import { DefaultErrorState } from 'components/common/Payable';
import { ErrorState } from 'types/Payable';
import {
  Deposit,
  DepositType,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';

interface ContractDepositProps {
  id: string;
  onValueChanged: (change: { id: string; value: ContractDepositModel }) => void;
  defaultValue: ContractDepositModel;
  validate: boolean;
  specialConditions: SpecialConditionData[];
  campaignId: string;
}

const ContractDeposit = ({
  id,
  onValueChanged,
  defaultValue,
  validate,
  specialConditions,
  campaignId,
}: ContractDepositProps) => {
  const { SalePrice } = useAppSelector(state => state.form.formValues);

  const [contractDeposit, setContractDeposit] = useState<ContractDepositModel>({
    initialDeposit: {
      ...(defaultValue?.initialDeposit ?? {}),
      depositType: DepositType.Initial,
    },
    balanceDeposit: { ...(defaultValue?.balanceDeposit ?? {}) },
  });

  const [errorState, setErrorState] = useState<ContractDepositErrorState>({
    initial: DefaultErrorState,
    balance: DefaultErrorState,
  });

  const ValidateContractDepositTotal = (
    initialDeposit: Deposit,
    balanceDeposit: Deposit
  ) => {
    const initialAmount = CalculateAmount(SalePrice, initialDeposit);
    const isInitialDepositInvalid =
      initialAmount > SalePrice * MaxDepositPercentage;
    let initialError: ErrorState = {
      depositError: isInitialDepositInvalid,
      message: isInitialDepositInvalid ? ErrorMessage : undefined,
    };

    const balanceAmount = CalculateAmount(SalePrice, balanceDeposit);
    let balanceError: ErrorState = DefaultErrorState;

    if (balanceAmount > 0) {
      const isBalanceDepositInvalid =
        initialAmount + balanceAmount > SalePrice * MaxDepositPercentage;
      balanceError = {
        depositError: isBalanceDepositInvalid,
        message: isBalanceDepositInvalid ? ErrorMessage : undefined,
      };
      initialError = balanceError;
    }

    setErrorState({
      ...errorState,
      initial: initialError,
      balance: balanceError,
    });
  };

  useEffect(() => {
    ValidateContractDepositTotal(
      contractDeposit?.initialDeposit,
      contractDeposit?.balanceDeposit
    );
    onValueChanged({ id, value: contractDeposit });
  }, [contractDeposit]);

  const onBalanceDepositChange = useCallback(
    (balanceDeposit: Deposit) => {
      setContractDeposit(prev => ({
        ...prev,
        balanceDeposit: balanceDeposit,
      }));
    },
    []
  );

  return (
    <>
      <DepositPayable
        key={DepositType.Initial}
        id={DepositType.Initial}
        label={`${DepositType.Initial} Deposit`}
        onValueChanged={({ value }) =>
          setContractDeposit({ ...contractDeposit, initialDeposit: value })
        }
        defaultValue={contractDeposit?.initialDeposit}
        validate={validate}
        required
        error={errorState.initial}
        campaignId={campaignId}
        specialConditions={specialConditions}
      />
      <AgxDivider expanded />
      <BalanceDeposit
        key={DepositType.Balance}
        id={DepositType.Balance}
        defaultValue={contractDeposit?.balanceDeposit}
        validate={validate}
        onValueChanged={onBalanceDepositChange}
        error={errorState.balance}
        specialConditions={specialConditions}
        campaignId={campaignId}
      />
    </>
  );
};

export default ContractDeposit;
